import { t } from '@repo/i18n';
import { OrganizationSchema, type SessionType, type UserModel } from '@repo/shared/schema';
import { UserRole } from '@repo/shared/types';
import z from 'zod';

export const ZCurrentPasswordSchema = z
  .string()
  .min(6, {
    get message() {
      return t('auth.mustBeAtLeast6CharactersInLength');
    },
  })
  .max(72);

export const ZSignInSchema = z.object({
  email: z.email().min(1),
  password: ZCurrentPasswordSchema,
  totpCode: z.string().trim().optional(),
  backupCode: z.string().trim().optional(),
  csrfToken: z.string().trim(),
});

export type TSignInSchema = z.infer<typeof ZSignInSchema>;

export const ZPasswordSchema = z
  .string()
  .min(8, {
    get message() {
      return t('auth.mustBeAtLeast8CharactersInLength');
    },
  })
  .max(72, {
    get message() {
      return t('auth.cannotBeMoreThan72CharactersInLength');
    },
  })
  .refine((value) => value.length > 25 || /[A-Z]/.test(value), {
    get message() {
      return t('auth.oneUppercaseCharacter');
    },
  })
  .refine((value) => value.length > 25 || /[a-z]/.test(value), {
    get message() {
      return t('auth.oneLowercaseCharacter');
    },
  })
  .refine((value) => value.length > 25 || /\d/.test(value), {
    get message() {
      return t('auth.oneNumber');
    },
  })
  .refine((value) => value.length > 25 || /[`~<>?,./!@#$%^&*()\-_"'+=|{}[\];:\\]/.test(value), {
    get message() {
      return t('auth.oneSpecialCharacterIsRequired');
    },
  });

export const ZSignUpSchema = z.object({
  firstName: z.string().min(1, {
    get message() {
      return t('auth.firstNameIsRequired');
    },
  }),
  middleName: z.string().optional(),
  lastName: z.string().min(1, {
    get message() {
      return t('auth.lastNameIsRequired');
    },
  }),
  phone: z.string().optional(),
  email: z.email(),
  password: ZPasswordSchema,
});

export type TSignUpSchema = z.infer<typeof ZSignUpSchema>;

export const ZRegisterOrganizationSchema = z.object({
  firstName: z.string().min(1, {
    get message() {
      return t('auth.firstNameIsRequired');
    },
  }),
  middleName: z.string().optional(),
  lastName: z.string().min(1, {
    get message() {
      return t('auth.lastNameIsRequired');
    },
  }),
  email: z.email().min(1),
  password: ZPasswordSchema,
  phone: z.string().optional(),
  organizationName: z.string().min(1, {
    get message() {
      return t('auth.organizationNameIsRequired');
    },
  }),
  organizationCountry: z.string().optional(),
  organizationDescription: z.string().optional(),
});
export type TRegisterOrganizationSchema = z.infer<typeof ZRegisterOrganizationSchema>;

export const ZForgotPasswordSchema = z.object({
  email: z.email().min(1),
});

export type TForgotPasswordSchema = z.infer<typeof ZForgotPasswordSchema>;

export const ZResetPasswordSchema = z.object({
  password: ZPasswordSchema,
  token: z.string().min(1),
});

export type TResetPasswordSchema = z.infer<typeof ZResetPasswordSchema>;

export const ZVerifyEmailSchema = z.object({
  token: z.string().min(1),
});

export type TVerifyEmailSchema = z.infer<typeof ZVerifyEmailSchema>;

export const ZResendVerifyEmailSchema = z.object({
  email: z.email().min(1),
});

export type TResendVerifyEmailSchema = z.infer<typeof ZResendVerifyEmailSchema>;

export const ZUpdatePasswordSchema = z.object({
  currentPassword: ZCurrentPasswordSchema,
  password: ZPasswordSchema,
});

export type TUpdatePasswordSchema = z.infer<typeof ZUpdatePasswordSchema>;

export type SessionUser = Pick<
  UserModel,
  | 'firstName'
  | 'middleName'
  | 'lastName'
  | 'email'
  | 'emailVerified'
  | 'disabled'
  | 'type'
  | 'twoFactorEnabled'
> & {
  id: string;
};

export type SessionValidationResult =
  | {
      session: SessionType;
      user: SessionUser;
      isAuthenticated: true;
    }
  | { session: null; user: null; isAuthenticated: false };

export const ZGetOrganizationSessionResponseSchema = OrganizationSchema.pick({
  id: true,
  name: true,
  createdAt: true,
})
  .extend({
    currentOrganizationRole: z.enum(UserRole),
    organizationId: z.cuid2(),
    banner: z.string().optional(),
    logo: z.string().optional(),
  })
  .array();

export type TGetOrganizationSessionResponse = z.infer<typeof ZGetOrganizationSessionResponseSchema>;
export type OrganizationSession = TGetOrganizationSessionResponse[number];

export type ActiveSession = {
  userId: string;
  expiresAt: Date;
  id?: string | null;
  createdAt?: Date | null;
  updatedAt?: Date | null;
  userAgent?: string | null;
  ipAddress?: string | null;
};

export const ZEnableTwoFactorRequestSchema = z.object({
  code: z.string().min(6).max(6),
});

export type TEnableTwoFactorRequestSchema = z.infer<typeof ZEnableTwoFactorRequestSchema>;

export const ZDisableTwoFactorRequestSchema = z.object({
  totpCode: z.string().trim().optional(),
  backupCode: z.string().trim().optional(),
});

export type TDisableTwoFactorRequestSchema = z.infer<typeof ZDisableTwoFactorRequestSchema>;

export const ZViewTwoFactorRecoveryCodesRequestSchema = z.object({
  token: z.string().trim().min(1),
});

export type TViewTwoFactorRecoveryCodesRequestSchema = z.infer<
  typeof ZViewTwoFactorRecoveryCodesRequestSchema
>;

export const ZActivateAccountSchema = z
  .object({
    token: z.string().min(1),
    email: z.email().min(1),
    password: ZPasswordSchema,
    confirmPassword: z.string().min(1, t('extra.pleaseConfirmYourPassword')),
  })
  .refine((data) => data.password === data.confirmPassword, {
    get message() {
      return t('auth.passwordsDoNotMatch');
    },
    path: ['confirmPassword'],
  });

export type TActivateAccountSchema = z.infer<typeof ZActivateAccountSchema>;
